import { Component, OnInit, OnDestroy } from '@angular/core';
import { AppService } from '../app.service';
import { Router } from "@angular/router";
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { HttpClient } from "@angular/common/http";
import { PlanGateService } from '../services/plan-gate.service';

type CheckoutState = 'idle' | 'processing' | 'success' | 'failed' | 'canceled' | 'slow';

interface Tier {
  name: string;
  monthly_token_limit: number;
  overage_rate_per_token?: number;
  purchasable?: boolean;
  amount: number | null;
  currency: string | null;
  interval: string | null;
}

@Component({
  selector: 'app-subscription',
  standalone: false,
  templateUrl: './subscription.component.html',
  styleUrls: ['./subscription.component.css']
})
export class SubscriptionComponent implements OnInit, OnDestroy {

  apiKey: any;
  workspaceName: any;
  status: any;
  usage: any;
  txt: any;

  /** Current tier name from the backend. Never assume - default to free. */
  plan: string = 'free';

  /** The one paid tier this deployment sells (starter OR pro). */
  paidTier: Tier | null = null;

  /** The free tier as the backend defines it. The comparison card used to
   *  hardcode its allowance, which silently went stale the moment the limit
   *  changed. */
  freeTier: Tier | null = null;

  loading = true;
  submitting = false;
  checkoutState: CheckoutState = 'idle';

  /** Set when the tier list could not be fetched at all (404, auth, network). */
  tiersError: string | null = null;

  private pollTimer: any = null;

  constructor(
    public appService: AppService,
    private http: HttpClient,
    public router: Router,
    public dialog: MatDialog,
    public planGate: PlanGateService
  ) { }

  async ngOnInit(): Promise<void> {
    this.readWorkspace();
    // Handle the Stripe redirect BEFORE loading usage, so a freshly completed
    // checkout is reflected on the first render rather than a beat later.
    this.handleCheckoutReturn();
    await Promise.all([this.getUsage(), this.getTiers()]);
    this.loading = false;
  }

  ngOnDestroy(): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
    }
  }

  // ---------------------------------------------------------------------
  // Derived state - the template binds to these, never to raw strings
  // ---------------------------------------------------------------------
  get isSubscribed(): boolean {
    return !!this.plan && this.plan.toLowerCase() !== 'free';
  }

  get isOnPaidTier(): boolean {
    return !!this.paidTier && this.plan?.toLowerCase() === this.paidTier.name.toLowerCase();
  }

  /** Upgrade is only offered when we know the tier AND the user is on free. */
  get canUpgrade(): boolean {
    return !this.loading
      && !this.submitting
      && !!this.paidTier
      && !this.isSubscribed
      && this.checkoutState !== 'processing';
  }

  get upgradeLabel(): string {
    if (this.isOnPaidTier) return 'Current plan';
    if (this.submitting) return 'Redirecting...';
    if (this.checkoutState === 'processing') return 'Confirming payment...';
    return `Upgrade to ${this.displayName(this.paidTier?.name)}`;
  }

  displayName(tier?: string | null): string {
    if (!tier) return 'Plan';
    const map: Record<string, string> = { starter: 'Personal', pro: 'Pro', free: 'Free' };
    return map[tier.toLowerCase()] || (tier.charAt(0).toUpperCase() + tier.slice(1));
  }

  /** The free allowance, straight from the tier table when it loaded. The
   *  fallback only shows when the list could not be fetched at all. */
  get freeMonthlyLimit(): number {
    return this.freeTier?.monthly_token_limit ?? 1000;
  }

  /** Cents -> "18.00". Returns null when the price could not be read. */
  get priceMajor(): string | null {
    if (!this.paidTier || this.paidTier.amount == null) return null;
    return (this.paidTier.amount / 100).toFixed(2);
  }

  // ---------------------------------------------------------------------
  // Checkout
  // ---------------------------------------------------------------------
  async selectPlan(tierName?: string) {
    const tier = tierName || this.paidTier?.name;

    if (!tier) {
      this.appService.addErrorMessage('No plan is available to purchase right now.');
      return;
    }

    // Client-side mirror of the backend guard. The backend is authoritative;
    // this just avoids a pointless round trip and a confusing error toast.
    if (this.isSubscribed) {
      this.appService.addErrorMessage(
        `You are already on the ${this.displayName(this.plan)} plan.`
      );
      return;
    }

    if (this.submitting) return;
    this.submitting = true;

    try {
      const response = await this.signedPost(
        { email: this.appService.get('xdbc.emailId'), tier },
        'usage/initiate-checkout'
      );

      const data = response?.data;

      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }

      // The backend now refuses to create a second subscription. Surface it
      // instead of failing silently, and resync so the UI stops offering it.
      if (data?.code === 'SUBSCRIPTION_EXISTS') {
        this.appService.addErrorMessage(
          data.error || 'You already have an active subscription.'
        );
        await this.getUsage();
        return;
      }

      throw new Error(data?.error || response?.message || 'Checkout could not be started');

    } catch (error: any) {
      this.txt = 'Activation failed: ' + (error.message || 'Unknown error');
      this.appService.addErrorMessage(
        'Failed to start checkout: ' + (error.message || 'Unknown error')
      );
    } finally {
      this.submitting = false;
    }
  }

  /**
   * Stripe sends the browser back to
   *   /brain/?checkout=success&ref=...&session_id=cs_...#/workspace
   * Query params sit BEFORE the fragment, so location.search works normally
   * even though the app uses hash routing.
   */
  private handleCheckoutReturn(): void {
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get('checkout');
    if (!outcome) return;

    const sessionId = params.get('session_id');
    const ref = params.get('ref');

    // Strip them immediately so a refresh does not replay the flow.
    this.clearQueryParams();

    if (outcome === 'cancel') {
      this.checkoutState = 'canceled';
      if (ref) this.reportCanceled(ref);
      return;
    }

    if (outcome === 'success' && sessionId) {
      this.checkoutState = 'processing';
      this.pollCheckout(sessionId, 0);
    }
  }

  private clearQueryParams(): void {
    const clean = window.location.pathname + window.location.hash;
    window.history.replaceState({}, document.title, clean);
  }

  /**
   * The redirect and the webhook race each other. Poll with backoff until the
   * backend reports a terminal state - it reconciles against Stripe directly,
   * so this resolves even if the webhook is delayed.
   */
  private async pollCheckout(sessionId: string, attempt: number): Promise<void> {
    try {
      const response = await this.signedPost({ sessionId }, 'usage/checkout-status');
      const status = response?.data?.status;

      if (status === 'completed') {
        this.checkoutState = 'success';
        await this.getUsage();
        this.appService.addSuccessMessage('Your plan is now active.');
        return;
      }

      if (status === 'failed' || status === 'expired' || status === 'canceled') {
        this.checkoutState = 'failed';
        return;
      }
    } catch (err) {
      // Transient - fall through and retry.
    }

    if (attempt >= 10) {
      this.checkoutState = 'slow';
      return;
    }

    const delay = Math.min(1000 * Math.pow(2, attempt), 5000);
    this.pollTimer = setTimeout(() => this.pollCheckout(sessionId, attempt + 1), delay);
  }

  private async reportCanceled(ref: string): Promise<void> {
    try {
      await this.signedPost({ ref }, 'usage/checkout-canceled');
    } catch (err) {
      // Best effort. If it fails the session expires at Stripe within 24h.
    }
  }

  dismissBanner(): void {
    this.checkoutState = 'idle';
  }

  /** The only other way off this page while the plan gate is closed. */
  async signOut(): Promise<void> {
    this.planGate.clear();
    this.appService.remove('xdbc.lastUrl');
    await this.appService.logOut();
  }

  async retryLoad(): Promise<void> {
    this.loading = true;
    this.tiersError = null;
    await Promise.all([this.getUsage(), this.getTiers()]);
    this.loading = false;
  }

  // ---------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------
  async getUsage(): Promise<void> {
    try {
      const response = await this.signedPost({}, 'usage/connector-summary');

      if (response && response.status === 'Success') {
        this.usage = response.data;
        this.plan = (response.data?.tier || 'free').toLowerCase();
        // This page is the one place the block can be lifted, so re-read the
        // gate from the same payload rather than waiting for its own TTL.
        this.planGate.applySummary(response.data);
      } else {
        throw new Error(response?.message);
      }
    } catch (error: any) {
      // Leave `plan` at its default so the UI never claims a paid tier it
      // could not confirm.
    }
  }

  async getTiers(): Promise<void> {
    try {
      const response = await this.signedPost({}, 'usage/tiers');

      // Tolerate either envelope: {data: {tiers: []}}, {tiers: []}, or a bare array
      const raw: any[] = response?.data?.tiers
        || response?.tiers
        || (Array.isArray(response?.data) ? response.data : []);

      // `free` has no Stripe price and is never purchasable.
      const sellable = raw.filter(t => t.purchasable && t.name?.toLowerCase() !== 'free');

      this.paidTier = sellable.length ? sellable[0] : null;
      this.freeTier = raw.find(t => t.name?.toLowerCase() === 'free') || null;
      this.tiersError = null;

      if (!this.paidTier) {
        console.warn('usage/tiers returned no purchasable tier', response);
      }
    } catch (error: any) {
      // Distinct from "none configured" -- we could not load the list at all.
      // Rendering these the same way makes a 404 look like a business rule.
      this.paidTier = null;
      this.freeTier = null;
      this.tiersError = error?.message || 'Could not load plans';
      console.error('Failed to load usage/tiers', error);
    }
  }

  // ---------------------------------------------------------------------
  // Cancellation
  // ---------------------------------------------------------------------
  openUnsubscribeDialog() {
    const dialogRef = this.dialog.open(UnsubscribeConfirmDialog, {
      panelClass: 'liam-dialog',
      data: {
        planName: this.displayName(this.plan),
        currentLimit: this.paidTier?.monthly_token_limit,
      }
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        await this.unsubscribe();
      }
    });
  }

  async unsubscribe() {
    try {
      // usage/cancel-subscription, not the legacy StripeService route: it takes
      // the workspace from the signed session rather than the request body.
      const res = await this.signedPost({}, 'usage/cancel-subscription');

      if (res?.status !== 'Success') {
        throw new Error(res?.message || 'Cancellation failed');
      }

      this.appService.addSuccessMessage(
        res?.message || 'Subscription cancelled. Access continues until the end of your billing cycle.'
      );

      // Do NOT optimistically set plan = 'free'. Cancellation takes effect at
      // period end, and the tier only changes when the webhook fires. Re-read
      // the real state instead of guessing.
      await this.getUsage();

    } catch (err) {
      this.appService.addErrorMessage('Unable to cancel subscription. Please try again or contact support.');
    }
  }

  openMessageDialog(): void {
    this.dialog.open(MessageDialog, { width: '500px', panelClass: 'liam-dialog' });
  }

  // ---------------------------------------------------------------------
  // Shared signed-request helper (this logic was duplicated in every call)
  // ---------------------------------------------------------------------
  private readWorkspace(): void {
    const wsJson = this.appService.get('xdbc.workspace');
    if (wsJson && typeof wsJson === 'string' && wsJson.trim() !== '' && wsJson !== 'undefined') {
      try {
        const ws = JSON.parse(wsJson);
        this.apiKey = ws.apiKey;
        this.status = ws.status;
        this.workspaceName = ws.workspaceName;
      } catch (err) {
        console.error('Invalid workspace JSON');
      }
    }
  }

  private async signedPost(payload: any, endpoint: string): Promise<any> {
    if (!this.apiKey) this.readWorkspace();

    const body = { ...payload, routeType: 'UI_SESSION' };

    const signatureData = await this.appService.wssign(body, this.workspaceName);
    if (!signatureData) {
      throw new Error('Failed to sign request');
    }
    const signature = new TextDecoder().decode(signatureData);

    return this.appService.wsPostRequestWithHeaders(body, endpoint, this.apiKey, signature);
  }
}


@Component({
  selector: 'unsubscribe-confirm-dialog',
  standalone: true,
  imports: [MatButtonModule],
  template: `
    <div class="dialog-container">
      <div class="dialog-logo">
        <svg width="56" height="56" viewBox="0 0 80 80" fill="none">
          <rect width="80" height="80" rx="18" fill="#f97316"/>
          <path d="M25 18V62H55" stroke="white" stroke-width="6" stroke-linecap="round" stroke-linejoin="round"/>
          <circle cx="25" cy="18" r="5" fill="white"/>
          <circle cx="55" cy="62" r="5" fill="white"/>
          <circle cx="40" cy="62" r="3" fill="white" opacity="0.6"/>
          <circle cx="25" cy="40" r="3" fill="white" opacity="0.6"/>
        </svg>
      </div>

      <h2 class="dialog-title">Cancel your subscription?</h2>

      <p class="dialog-message">
        You'll return to the <b>Free plan</b> at the end of your current billing cycle.
      </p>
      <p class="dialog-subtext">
        You keep every paid benefit until then. After that your monthly allocation returns to the free tier limit.
      </p>

      <div class="dialog-actions">
        <button class="btn-keep" (click)="dialogRef.close(false)">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
          </svg>
          Keep my plan
        </button>
        <button class="btn-cancel-sub" (click)="dialogRef.close(true)">Cancel subscription</button>
      </div>
    </div>
  `,
  styles: [`
    .dialog-container { font-family: 'Plus Jakarta Sans', system-ui, sans-serif; padding: 32px; background: #fff; border-radius: 20px; text-align: center; max-width: 420px; }
    .dialog-logo { margin-bottom: 20px; }
    .dialog-logo svg { filter: drop-shadow(0 4px 12px rgba(249,115,22,.25)); }
    .dialog-title { margin: 0 0 16px; font-size: 22px; font-weight: 700; color: #1a1a1a; }
    .dialog-message { margin: 0 0 8px; font-size: 15px; color: #555; line-height: 1.6; }
    .dialog-message b { color: #f97316; }
    .dialog-subtext { margin: 0 0 24px; font-size: 14px; color: #888; line-height: 1.5; }
    .dialog-actions { display: flex; flex-direction: column; gap: 12px; }
    .btn-keep { display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; background: linear-gradient(135deg,#f97316 0%,#ea580c 100%); color: #fff; border: none; padding: 14px 24px; font-size: 15px; font-weight: 600; border-radius: 12px; cursor: pointer; transition: all .3s ease; box-shadow: 0 4px 15px rgba(249,115,22,.35); }
    .btn-keep:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(249,115,22,.45); }
    .btn-keep svg { width: 18px; height: 18px; }
    .btn-cancel-sub { width: 100%; background: #f8f9fa; border: 1px solid #e9ecef; color: #666; padding: 14px 24px; font-size: 14px; font-weight: 500; border-radius: 12px; cursor: pointer; transition: all .3s ease; }
    .btn-cancel-sub:hover { background: #f1f3f4; border-color: #ddd; color: #333; }
    @media (max-width: 480px) { .dialog-container { padding: 24px; } .dialog-title { font-size: 20px; } }
  `]
})
export class UnsubscribeConfirmDialog {
  constructor(public dialogRef: MatDialogRef<UnsubscribeConfirmDialog>) { }
}


@Component({
  selector: 'message-dialog',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule],
  template: `
    <div class="dialog-container">
      <div class="dialog-header">
        <svg class="logo" width="56" height="56" viewBox="0 0 80 80" fill="none">
          <rect width="80" height="80" rx="18" fill="#f97316"/>
          <path d="M25 18V62H55" stroke="white" stroke-width="6" stroke-linecap="round" stroke-linejoin="round"/>
          <circle cx="25" cy="18" r="5" fill="white"/>
          <circle cx="55" cy="62" r="5" fill="white"/>
          <circle cx="40" cy="62" r="3" fill="white" opacity="0.6"/>
          <circle cx="25" cy="40" r="3" fill="white" opacity="0.6"/>
        </svg>
        <h2 class="dialog-title">Thanks for your interest!</h2>
        <p class="dialog-highlight">We'll be in touch soon.</p>
      </div>

      <div class="dialog-content">
        <p class="dialog-message">
          Our team will reach out shortly to discuss how the Enterprise Plan can be customized for your organization's needs.
          In the meantime, feel free to continue exploring what <strong>LIAM</strong> can do.
        </p>

        <button class="btn-continue" (click)="onClose()">
          Continue Exploring
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </button>
      </div>
    </div>
  `,
  styles: [`
    .dialog-container { font-family: 'Plus Jakarta Sans', system-ui, sans-serif; padding: 0; background: #fff; border-radius: 20px; overflow: hidden; max-width: 440px; }
    .dialog-header { background: linear-gradient(135deg,#f97316 0%,#ea580c 100%); padding: 32px 24px; text-align: center; position: relative; }
    .logo { margin-bottom: 16px; position: relative; z-index: 1; filter: drop-shadow(0 4px 12px rgba(0,0,0,.15)); }
    .dialog-title { margin: 0; font-size: 24px; font-weight: 700; color: #fff; position: relative; z-index: 1; }
    .dialog-highlight { margin: 8px 0 0; font-size: 16px; color: rgba(255,255,255,.9); position: relative; z-index: 1; }
    .dialog-content { padding: 28px 24px; text-align: center; }
    .dialog-message { margin: 0 0 24px; font-size: 15px; color: #555; line-height: 1.6; }
    .dialog-message strong { color: #f97316; }
    .btn-continue { display: inline-flex; align-items: center; justify-content: center; gap: 10px; background: linear-gradient(135deg,#f97316 0%,#ea580c 100%); color: #fff; border: none; padding: 14px 32px; font-size: 15px; font-weight: 600; border-radius: 12px; cursor: pointer; transition: all .3s ease; box-shadow: 0 4px 15px rgba(249,115,22,.35); }
    .btn-continue:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(249,115,22,.45); }
    .btn-continue svg { width: 18px; height: 18px; }
    @media (max-width: 480px) { .dialog-header { padding: 24px 20px; } .dialog-title { font-size: 20px; } .dialog-content { padding: 24px 20px; } }
  `]
})
export class MessageDialog {

  msg: any;

  constructor(
    public dialogRef: MatDialogRef<MessageDialog>,
    public appService: AppService,
    public router: Router
  ) { }

  ngOnInit(): void {
    this.getMessage();
  }

  getMessage() {
    try {
      this.appService.postRequest({}, 'UserService/ShowInterestInEnterprisePlan').then(result => {
        if (result) this.msg = result.message;
      });
    } catch (error) {
      console.error('Error registering enterprise interest');
    }
  }

  onClose(): void {
    this.dialogRef.close();
    this.router.navigate(['/subscription']);
  }
}